import { useEffect, useState } from 'react';
import { connectionsApi } from '@/api/connections';
import type { ColumnDesignInfo } from '@/types/domain';
import './TableDesignModal.css';

interface Props {
  connectionId: string;
  schema: string;
  table: string;
  onClose: () => void;
}

function formatType(col: ColumnDesignInfo): string {
  if (col.maxLength !== null) {
    const len = col.maxLength === -1 ? 'MAX' : String(col.maxLength);
    return `${col.dataType}(${len})`;
  }
  if (col.numericPrecision !== null && col.numericScale !== null && col.numericScale > 0) {
    return `${col.dataType}(${col.numericPrecision},${col.numericScale})`;
  }
  if (col.numericPrecision !== null && col.dataType.toLowerCase().includes('decimal')) {
    return `${col.dataType}(${col.numericPrecision})`;
  }
  return col.dataType;
}

export function TableDesignModal({ connectionId, schema, table, onClose }: Props) {
  const [columns, setColumns] = useState<ColumnDesignInfo[] | null>(null);
  const [error, setError]     = useState<string | null>(null);

  useEffect(() => {
    connectionsApi.getTableDesign(connectionId, schema, table)
      .then(setColumns)
      .catch(err => setError(err?.response?.data?.error ?? err?.message ?? 'Erro ao carregar design'));
  }, [connectionId, schema, table]);

  return (
    <div className="tdm-overlay">
      <div className="tdm-panel">
        <div className="tdm-header">
          <span className="tdm-title">
            <span className="tdm-title-icon">▤</span>
            {schema}.{table}
            {columns && (
              <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>
                — {columns.length} coluna{columns.length !== 1 ? 's' : ''}
              </span>
            )}
          </span>
          <button className="tdm-close" onClick={onClose}>×</button>
        </div>

        <div className="tdm-body">
          {!columns && !error && <div className="tdm-loading">Carregando...</div>}
          {error && <div className="tdm-error">{error}</div>}
          {columns && (
            <table className="tdm-table">
              <thead>
                <tr>
                  <th className="td-pk">PK</th>
                  <th>Coluna</th>
                  <th>Tipo</th>
                  <th>Null</th>
                  <th>Default</th>
                </tr>
              </thead>
              <tbody>
                {columns.map(col => (
                  <tr key={col.name} className={col.isPrimaryKey ? 'is-pk' : ''}>
                    <td className="td-pk">{col.isPrimaryKey ? '●' : ''}</td>
                    <td className="col-name">{col.name}</td>
                    <td className="col-type">{formatType(col)}</td>
                    <td className={col.nullable ? 'col-null-yes' : 'col-null-no'}>
                      {col.nullable ? 'NULL' : 'NOT NULL'}
                    </td>
                    <td className="col-default">{col.defaultValue ?? ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
